import { findKey } from 'lodash-es';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
	DEFAULT_LANGUAGE,
	DEFAULT_LANGUAGE_ROUTE,
	localesTransMap,
	SUPPORTED_LANGUAGES_MAP_ROUTES,
	SupportedLanguages,
} from '@shared/i18n/config/language';
import { getDefaultLanguage } from './utils/default';
import { isMobile } from 'react-device-detect';

export const getLangPrefixByPathname = () => {
	const langMatch = location.pathname.match(/^\/([^/]+)/);
	const lang = langMatch ? langMatch[1] : '';
	return lang;
};

export function getI18nSetting(): SupportedLanguages {
	const lang = getLangPrefixByPathname();
	const currentLanguage = lang ? lang : getDefaultLanguage();
	const navigator = (localesTransMap as Record<string, string>)[window.navigator.language];
	const supportedLang = SUPPORTED_LANGUAGES_MAP_ROUTES[currentLanguage] ?? DEFAULT_LANGUAGE;
	return (supportedLang ?? navigator) as SupportedLanguages;
}

/** @description: 路由跳转 */
let navigate: NavigateFunction = () => {
	if (isMobile) console.error('navigate 不可用, 你应该使用 landingBack');
};

type Callback = (...args: any[]) => void;

export const getLocalizedUrl = (url: string) => {
	const currentLang = getI18nSetting();
	const supportedLang = findKey(SUPPORTED_LANGUAGES_MAP_ROUTES, (item) => item === currentLang);
	const regex = new RegExp(`^/${supportedLang}`);

	if (supportedLang && supportedLang !== DEFAULT_LANGUAGE_ROUTE && !regex.test(url)) {
		return !url?.startsWith('/')
			? `/${supportedLang}/${url}`
			: `/${supportedLang}${url === '/' ? '' : url}`;
	} else {
		return url;
	}
};

export const goto = (path: string, data?: any, cb?: Callback) => {
	const newUrl = getLocalizedUrl(path);
	navigate(newUrl, { state: data });
	cb && cb();
};

export const redirectUrl = (path: string) => {
	const isFullUrl = path.startsWith('http://') || path.startsWith('https://');
	const newUrl = isFullUrl ? path : getLocalizedUrl(path);
	window.location.href = newUrl;
};

export const openLink = (url: string, cb?: Callback) => {
	const isFullUrl = url.startsWith('http://') || url.startsWith('https://');
	const newUrl = isFullUrl ? url : getLocalizedUrl(url);
	window.open(newUrl, '_blank');
	cb && cb();
};

export const langReplaceUrl = (data: any, unused: string, url: string) => {
	const newUrl = getLocalizedUrl(url);
	history.replaceState(data, unused, newUrl);
};

export const langPushUrl = (data: any, unused: string, url: string) => {
	const newUrl = getLocalizedUrl(url);
	history.pushState(data, unused, newUrl);
};

export const landingBack = (path: string = '/') => {
	window.location.href = getLocalizedUrl(path);
};
/**
 * 页面回退
 * @param callback 可选的回调函数，接收回退后的pathname
 */
export const goBack = async (callback?: (newPath: string) => void) => {
	// 监听popstate事件，这个事件会在页面导航完成后触发
	const handlePopstate = () => {
		// 获取回退后的路径
		let path = window.location.pathname;
		const langPrefix = getLangPrefixByPathname();
		if (langPrefix && SUPPORTED_LANGUAGES_MAP_ROUTES[langPrefix]) {
			path = path.replace(langPrefix, '');
		}

		// 执行回调函数
		if (typeof callback === 'function') {
			callback(path);
		}

		// 移除事件监听器，确保只执行一次
		window.removeEventListener('popstate', handlePopstate);
	};

	// 添加一次性事件监听器
	window.addEventListener('popstate', handlePopstate);

	// 执行回退操作
	window.history.back();
};

export const SeoBack = () => {
	const ref = document.referrer;
	let isInternal = false;
	if (!ref) {
		// 直接打开的。回首页
		isInternal = false;
	} else {
		try {
			const refURL = new URL(ref);
			// 判断是站内开的还是google等其他的地方进来的
			isInternal = refURL.origin === location.origin;
		} catch {
			isInternal = false;
		}
	}
	// 站内也有可以新标签页打开。所以要判断一下opener，这种情况也要回首页，
	if (isInternal && window.opener === null) {
		// 来自本站 → 回退
		goBack();
	} else {
		// 外部来源或直接打开 → 回首页
		goto('/');
	}
};

const WithRouter = () => {
	// eslint-disable-next-line react-hooks/globals
	navigate = useNavigate();
	return <></>;
};

export default WithRouter;
